import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import BackupIcon from '@mui/icons-material/Backup';
import SpeedIcon from '@mui/icons-material/Speed';


const services = [
  'Hard drive data recovery',
  'SSD data recovery',
  'RAID data recovery',
  'Flash drive recovery',
  'Memory card recovery',
  'Smartphone data recovery',
  'Deleted file recovery',
  'Corrupted file repair',
  'Email data recovery',
  'Database recovery'
];

const benefits = [
  {
    icon: SecurityIcon,
    title: 'Secure Recovery',
    description: 'Your data is handled with utmost security and confidentiality'
  },
  {
    icon: SpeedIcon,
    title: 'Fast Service',
    description: 'Quick recovery process with emergency service available'
  },
  {
    icon: StorageIcon,
    title: 'All Devices',
    description: 'Recovery from any storage device or system'
  },
  {
    icon: BackupIcon,
    title: 'Safe Process',
    description: 'Non-destructive recovery methods used'
  }
];

const specializations = [
  {
    title: 'Physical Damage',
    description: 'Recovery from physically damaged drives'
  },
  {
    title: 'Logical Issues',
    description: 'Recovery from formatting and file system errors'
  },
  {
    title: 'RAID Systems',
    description: 'Complex RAID array recovery'
  },
  {
    title: 'Encrypted Data',
    description: 'Recovery of encrypted and protected data'
  }
];

function DataRecovery() {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            className="gradient-text"
            sx={{ mb: 3 }}
          >
            Professional Data Recovery
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Expert data recovery services for all storage devices
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 4,
              fontSize: '1.2rem',
              background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
              }
            }}
          >
            Book Recovery Service
          </Button>
        </Box>

        {/* Benefits Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="glass-effect card-hover">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2, color: 'primary.main' }}>
                    {React.createElement(benefit.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Services List */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 8,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Recovery Services
          </Typography>
          <Grid container spacing={2}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                </List>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Specializations */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Our Expertise
          </Typography>
          <Grid container spacing={3}>
            {specializations.map((spec, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  className="glass-effect"
                  sx={{ 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    p: 2
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {spec.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {spec.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default DataRecovery;
